import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Key, Pencil, Trash2, Shield, Phone, Mail } from "lucide-react";

const ROLES = [
    { code: "secretary", label: "Sekretar", tone: "bg-sand/40 text-amber-800 border-sand/60" },
    { code: "quality_coordinator", label: "Koordinator Cilësie", tone: "bg-seablue/15 text-seablue border-seablue/30" },
    { code: "administrata", label: "Administrata", tone: "bg-oxford/10 text-oxford border-oxford/30" },
    { code: "sigurimi", label: "Sigurimi", tone: "bg-terracotta/10 text-terracotta border-terracotta/30" },
    { code: "drejtoresha", label: "Drejtoresha", tone: "bg-sage/15 text-sage border-sage/30" },
];

const DEFAULT_PASSWORDS = {
    secretary: "sekretari123",
    quality_coordinator: "koordinatori123",
    administrata: "administrata123",
    sigurimi: "sigurimi123",
    drejtoresha: "drejtoresha123",
};

const ConfirmButton = ({ onConfirm, testId, label = "Hiq" }) => {
    const [armed, setArmed] = useState(false);
    return (
        <button type="button" onClick={() => { if (armed) { onConfirm(); setArmed(false); } else { setArmed(true); setTimeout(() => setArmed(false), 3000); } }} data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition ${armed ? "bg-terracotta text-white" : "bg-terracotta/10 text-terracotta hover:bg-terracotta/20"}`}>
            <Trash2 className="w-3 h-3" /> {armed ? "Konfirmo" : label}
        </button>
    );
};

export default function StaffTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "administrata123", role: "administrata", phone: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [pwdUser, setPwdUser] = useState(null);
    const [pwdInput, setPwdInput] = useState("");
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "", role: "", phone: "" });

    const refresh = async () => {
        setLoading(true);
        try { const r = await api.get("/admin/staff"); setItems(r.data || []); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const changeRole = (role) => setForm({ ...form, role, password: DEFAULT_PASSWORDS[role] || "password123" });

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/admin/users", form);
            setOpen(false);
            setForm({ name: "", email: "", password: "administrata123", role: "administrata", phone: "" });
            refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    const submitPwd = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/users/${pwdUser.id}/password`, { new_password: pwdInput });
            setPwdUser(null); setPwdInput("");
            alert("Fjalëkalimi u ndryshua.");
        } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };
    const submitEdit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/admin/users/${editUser.id}`, editForm);
            setEditUser(null); refresh();
        } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };
    const del = async (u) => {
        try { await api.delete(`/admin/users/${u.id}`); refresh(); }
        catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
    };

    const filtered = filter === "all" ? items : items.filter((u) => u.role === filter);

    return (
        <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={() => setFilter("all")}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filter === "all" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`}
                    data-testid="staff-filter-all">
                    Të gjithë ({items.length})
                </button>
                {ROLES.map((r) => {
                    const n = items.filter((i) => i.role === r.code).length;
                    return (
                        <button key={r.code} onClick={() => setFilter(r.code)}
                            className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filter === r.code ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`}
                            data-testid={`staff-filter-${r.code}`}>
                            {r.label} ({n})
                        </button>
                    );
                })}
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="ml-auto bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="staff-add-btn">
                            <Plus className="w-4 h-4" /> Shto Anëtar Stafi
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading">Krijo llogari stafi</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Input placeholder="Emri i plotë" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-form-name" />
                            <Input type="email" placeholder="Emaili" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="staff-form-email" />
                            <Input placeholder="Telefon (opsional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-form-phone" />
                            <select value={form.role} onChange={(e) => changeRole(e.target.value)}
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="staff-form-role">
                                {ROLES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                            </select>
                            <Input placeholder="Fjalëkalimi" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="staff-form-password" />
                            {err && <div className="text-xs text-terracotta">{err}</div>}
                            <DialogFooter>
                                <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="staff-form-submit">
                                    {busy ? "Duke krijuar…" : "Krijo"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? <div className="p-6 text-slate-500">Duke ngarkuar…</div> :
                 filtered.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë anëtar stafi.</div> : (
                    <div className="divide-y divide-slate-200">
                        {filtered.map((u) => {
                            const r = ROLES.find((x) => x.code === u.role) || { label: u.role, tone: "bg-slate-100 text-slate-700 border-slate-200" };
                            return (
                                <div key={u.id} className="flex items-center gap-3 px-4 sm:px-6 py-3" data-testid={`staff-row-${u.id}`}>
                                    <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center shrink-0">
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-slate-900 truncate">{u.name}</span>
                                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${r.tone}`}>{r.label}</span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
                                            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>
                                            {u.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone}</span>}
                                        </div>
                                    </div>
                                    <button onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role, phone: u.phone || "" }); }}
                                        className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Edito" data-testid={`staff-edit-${u.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => { setPwdUser(u); setPwdInput(""); }}
                                        className="p-1.5 rounded-md text-seablue hover:bg-seablue/10" title="Ndrysho fjalëkalimin" data-testid={`staff-pwd-${u.id}`}>
                                        <Key className="w-4 h-4" />
                                    </button>
                                    <ConfirmButton onConfirm={() => del(u)} testId={`staff-delete-${u.id}`} label="Fshi" />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Dialog open={!!pwdUser} onOpenChange={(v) => !v && setPwdUser(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Ndrysho fjalëkalimin për {pwdUser?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitPwd} className="space-y-3">
                        <Input type="password" placeholder="Fjalëkalimi i ri (min 6)" value={pwdInput} onChange={(e) => setPwdInput(e.target.value)} required minLength={6} data-testid="staff-pwd-input" />
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="staff-pwd-submit">Ndrysho</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito {editUser?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <Input placeholder="Emri" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="staff-edit-name" />
                        <Input type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="staff-edit-email" />
                        <Input placeholder="Telefon (opsional)" value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="staff-edit-phone" />
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Roli</label>
                            <select value={editForm.role || ""} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="staff-edit-role">
                                {ROLES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                            </select>
                        </div>
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="staff-edit-submit">Ruaj</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
